import get from 'lodash/get';
import pick from 'lodash/pick';
import moment from 'moment';
import {useState} from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DateField } from '@mui/x-date-pickers/DateField';
import Grid2 from "@mui/material/Unstable_Grid2";
import Divider from "@mui/material/Divider";

import AddressField from '../common/AddressField';
import CustomInput from '../common/CustomInput';
import CustomSelect from '../common/CustomSelect';
import CustomTextField from '../common/CustomTextField';
import PhoneInput from '../common/PhoneInput';
import StateSelect from '../common/StateSelect';
import getPronouns from '../../helpers/getPronouns';
import * as formValidations from '../../helpers/validations';
import BackButton from "../common/BackButton";
import useIsMobile from "../../hooks/useIsMobile";
import './AccountSettingsScreen.scss'

const getFields = () => ([
  {
    name: 'phone',
    label: 'Personal Phone',
    type: 'phone',
    placeholder: '(123) 456-7890',
    required: true,
    validate: formValidations.phone,
    orderIndex: 1,
  },
  {
    name: 'address',
    type: 'address',
    placeholder: null,
    required: true,
    validate: () => true,
    orderIndex: 2,
  },
  {
    name: 'gender',
    label: 'Gender',
    type: 'options',
    required: false,
    validate: () => true,
    options: [
      {value: 'female', label: 'Female'},
      {value: 'male', label: 'Male'},
      {value: 'nonbinary', label: 'Non-binary'},
    ],
    orderIndex: 3,
  },
  {
    name: 'personal_pronouns',
    label: 'Personal Pronouns',
    type: 'options',
    required: false,
    validate: () => true,
    options: getPronouns(),
    orderIndex: 4,
  },
  {
    name: 'npi_number',
    label: 'NPI Number',
    type: 'text',
    placeholder: '123456789',
    required: false,
    validate: () => true,
    orderIndex: 5,
  },
  {
    name: 'license_numbers',
    label: 'License Number',
    type: 'text',
    placeholder: 'State License #',
    required: false,
    validate: () => true,
    orderIndex: 6,
  },
]);

const defaultFormValues = getFields().reduce((acc, next) => ({
  ...acc,
  [next.name]: '',
}), {});

const sortFields = ({isMobile}) => (fields) => {
  if (isMobile) {
    return fields.sort((a, b) => a.orderIndex - b.orderIndex);
  }
  return fields;
}

const FieldInput = props => {
  const [blurred, setBlurred] = useState(false);
  const childProps = {
    ...props,
    error: !props.validate(props.value) && blurred,
    onBlur: () => setBlurred(true),
  };
  if (props.type === 'text') return <CustomTextField {...childProps} />;
  if (props.type === 'phone') return <PhoneInput {...childProps} />;
  if (props.type === 'options') return <CustomSelect {...childProps} />;
  if (props.type === 'state') return <StateSelect {...childProps} />;
  if (props.type === 'address') {
    const addressFields = [
      'address_line_1', 
      'address_line_2', 
      'address_city', 
      'address_state', 
      'address_zip',
      'lat',
      'lon',
    ];
    return (
      <AddressField 
        {...childProps}
        showLabel={true}
        defaultValue={pick(props.formValues || {}, ...addressFields)}
        onChange={address => props.onChange({ target: {value: address} })}
      />
    );
  }
  if (props.type === 'date') {
    return (
      <CustomInput
        id={props.name}
        label={props.label}
        variant='outlined'
        required={props.required}
      >
        <DateField
          required={props.required}
          defaultValue={props.defaultValue ? moment(props.defaultValue) : null}
          onChange={e => props.onChange({
            target: {value: e.format('MM/DD/yyyy')}
          })}
          sx={{bgcolor: '#ffffff'}}
        />
      </CustomInput>
    );
  }
  return <></>;
};

const handleFieldUpdate = (formValues, setFormValues) => fieldName => e => {
  if (fieldName !== 'address') {
    const value = get(e, 'target.value', '');
    const values = {
      ...formValues,
      [fieldName]: value,
    };
    return setFormValues(values);
  }
  const value = get(e, 'target.value', {});
  const values = {...formValues, ...value};
  return setFormValues(values);
};

const parseFormValues = formValues => {
  let parsedForm = {...formValues};
  return parsedForm;
};

const AccountSettingsScreen = props => {
  const {provider} = props;
  const isMobile = useIsMobile();
  const fields = getFields();
  const sortedFields = sortFields({isMobile})(fields);
  const priorValues = (
    provider
    ? pick(
      provider,
      'first_name', 'last_name', 'phone',
      'address_city', 'address_zip', 'address_state',
      'address_line_1', 'address_line_2',
      'personal_pronouns', 'gender',
      'lat', 'lon',
    )
    : null
  );
  const [formValues, setFormValues] = useState(priorValues || defaultFormValues);
  return (
    <Container className='AccountSettingsScreen' maxWidth=''>
      <Box>
        <BackButton />
      </Box>
      <Box className='AccountSettingsScreen__content'>
        <Stack spacing={2} padding={{xs: 1, sm: 2, md: 3}}>
          <Typography
            variant='h1'
            className='AccountSettingsScreen__header'
          >
            Personal Information
          </Typography>
          <Divider/>
          <Grid2 container spacing={2}>
            {sortedFields.map((f, index) =>
              <Grid2 key={index} xs={12} sm={6} >
                <FieldInput
                  fullWidth={true}
                  value={get(formValues, f.name)}
                  defaultValue={get(provider, f.name)}
                  onChange={handleFieldUpdate(formValues, setFormValues)(f.name)}
                  formValues={formValues}
                  {...f}
                />
              </Grid2>
            )}
          </Grid2>
          <Stack alignItems="center">
            <Button
              className='AccountSettingsScreen__save-btn'
              variant='primaryGradient'
              onClick={() => props.onSubmit(parseFormValues(formValues))}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Container>
  )
};

export default AccountSettingsScreen;
